import _ from "lodash";

export const NODE_PADDING_TOP = 10;
export const NODE_PADDING_BOTTOM = 20;
export const NODE_PADDING_LEFT = 10;
export const NODE_PADDING_RIGHT = 10;
export const BUTTON_PADDING_TOP = 10;
export const BUTTON_HEIGHT = 24;
export const BUTTON_ROW_GAP = 8;
export const TEXT_LINE_HEIGHT = 16
export const NODE_MENU_OFFSET_Y = -15;

export const OUTPUT_HEIGHT = 5

export enum NodeType {
    MESSAGE = "message",
    ACTION = "action",
    DELAY = "delay",
    CONDITION = "condition",
    NOTE = "note",
}

export type Node = {
    id: string;
    x: number;
    y: number;
    w: number;
    h: number;
    type: NodeType;
    text: string;
    image: string | null;
    buttons?: ButtonData[];
    layout?: NodeLayout;   // 👈 сюда кладём рассчитанные области
    data?: NodeData;
    stats?: {
        delivered: number;
    };
};

export type ButtonData = {
    id: string;
    text: string;
    url: string;
    width?: number;
    height?: number;
    stats?: {
        ctr: number;
    };
}

export type NodeLayout = {
    deleteNodeIcon?: Rect;
    titleRect?: Rect;
    textRect?: Rect;
    buttonRects?: Rect[];
    buttonCTRRect?: Rect[];
    addButtonRect?: Rect;
    buttonOutPoint?: Rect[];
    buttonInPoint?: Rect;
    buttonDeletes?: Rect[];
    edgeTrashRect?: Rect;
    outPoint?: Rect;
    bodyRect?: Rect;

    // Stats layout
    statsRect?: Rect;

    // Condition layout
    branchRects?: Rect[];
    branchDeletes?: Rect[];
    branchOutPoint?: Rect[];
    addBranchRect?: Rect;
};

type Rect = { x: number; y: number; w: number; h: number };

export type Point = { x: number; y: number };

export interface NodeData {
    type: string;
    payload: any;
}

// Стрелка
export type EdgeLayout = {
    trashRect?: Rect;
};
export type Edge = {
    fromNodeID: string; // node.id    
    toNodeID: string; // node.id
    layout?: EdgeLayout;
    source?: {
        type: "button" | "branch";
        id: string;
    };
};

/**
 * Обычно:
 * выход → справа
 * вход → слева
 */
export const getOutPoint = (node: Node) => {
    return {
        x: node.x + node.w - 10,
        y: node.y + node.h - NODE_PADDING_BOTTOM,
        w: 5,
        h: 5,
    }
}
export const getInPoint = (node: Node) => ({
    x: node.x - 5,
    y: node.y + node.h / 2,
});


export const getButtonOutputPoint = (
    ctx: CanvasRenderingContext2D,
    node: Node,
    buttonIndex: number
) => {
    const textHeight = measureTextHeight(
        ctx,
        node.text || "",
        node.w
    );

    const y =
        node.y +
        NODE_PADDING_TOP +
        textHeight +
        BUTTON_PADDING_TOP +
        buttonIndex * (BUTTON_HEIGHT + BUTTON_ROW_GAP) +
        BUTTON_HEIGHT / 2;

    const x = node.x + node.w;

    return { x, y };
};




// Функция отрисовки кривой стрелки
export const drawCurvedArrow = (
    ctx: CanvasRenderingContext2D,
    from: { x: number; y: number },
    to: { x: number; y: number }
) => {
    const midY = (from.y + to.y) / 2;

    // контрольная точка
    const cpx = from.x;
    const cpy = midY;

    ctx.strokeStyle = "#64748b";
    ctx.lineWidth = 2;

    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.quadraticCurveTo(cpx, cpy, to.x, to.y);
    ctx.stroke();

    drawArrowHead(ctx, from, to);
};

// Наконечник стрелки (по касательной)
function drawArrowHead(
    ctx: CanvasRenderingContext2D,
    tip: Point,      // точка касания node
    dir: Point       // нормализованное направление
) {
    const size = 8;
    const spread = Math.PI / 6;
    const angle = Math.atan2(dir.y, dir.x);

    ctx.beginPath();
    ctx.moveTo(tip.x, tip.y); // 👈 касается node
    ctx.lineTo(
        tip.x - size * Math.cos(angle - spread),
        tip.y - size * Math.sin(angle - spread)
    );
    ctx.lineTo(
        tip.x - size * Math.cos(angle + spread),
        tip.y - size * Math.sin(angle + spread)
    );
    ctx.closePath();

    ctx.fillStyle = "#64748b";
    ctx.fill();
}


function normalize(v: { x: number; y: number }) {
    const len = Math.hypot(v.x, v.y) || 1;
    return { x: v.x / len, y: v.y / len };
}


// Красивая cubic-кривая вниз
// 📌 Обрати внимание:
// наконечник рисуется по направлению последнего сегмента, а не from → to.
export const drawCubicArrow = (
    ctx: CanvasRenderingContext2D,
    from: { x: number; y: number },
    to: { x: number; y: number },
    onMiddle?: (pos: Point) => void
) => {
    const offset = Math.abs(to.y - from.y) * 0.5;

    const p0 = from;
    const p1 = { x: from.x, y: from.y + offset };
    const p2 = { x: to.x, y: to.y - offset };
    const p3 = to;

    ctx.strokeStyle = "#64748b";
    ctx.lineWidth = 1;

    // линия
    ctx.beginPath();
    ctx.moveTo(p0.x, p0.y);
    ctx.bezierCurveTo(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y);
    ctx.stroke();

    // стрелка
    const dir = normalize({ x: p3.x - p2.x, y: p3.y - p2.y });

    drawArrowHead(ctx, p3, dir);


    // ИКОНКА стрелки
    const t = 0.5; // середина
    const pos = cubicPoint(p0, p1, p2, p3, t);
    onMiddle?.(pos);

    const size = 8;

    ctx.save();
    ctx.translate(pos.x, pos.y);
    const trashIcon = new Image();
    trashIcon.src = "/media/images/trash.png";
    ctx.drawImage(trashIcon, -size / 2, -size / 2, size, size);
    ctx.restore();
};


// Маленький кружок снизу (hit-test): справа ноды крайний правый угол
export const isOnOutput = (node: Node, x: number, y: number) => {
    const r = 8;
    const cx = node.x + node.w;
    const cy = node.y + node.h / 2;

    // Math.hypot - расстояние от клика до центра
    return Math.hypot(x - cx, y - cy) <= r;
};

export const isOnOutputMessage = (node: Node, x: number, y: number) => {
    const r = 8;
    const cx = node.x + node.w;
    const cy = node.y + node.h / 2;

    return Math.hypot(x - cx, y - cy) <= r;
};
// проверяем расстояние от клика до сегментов
export const distanceToSegment = (
    px: number,
    py: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number
) => {
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let t = dot / lenSq;

    t = Math.max(0, Math.min(1, t));

    const x = x1 + t * C;
    const y = y1 + t * D;

    return Math.hypot(px - x, py - y);
};

// Проверка попадания в Bezier
export const isPointOnBezier = (
    mouse: { x: number; y: number },
    from: { x: number; y: number },
    to: { x: number; y: number },
) => {
    const offset = Math.max(60, Math.abs(to.y - from.y) * 0.5);

    const cp1 = { x: from.x, y: from.y + offset };
    const cp2 = { x: to.x, y: to.y - offset };

    const steps = 20;
    let prev = from;

    for (let i = 1; i <= steps; i++) {
        const t = i / steps;

        const x =
            Math.pow(1 - t, 3) * from.x +
            3 * Math.pow(1 - t, 2) * t * cp1.x +
            3 * (1 - t) * Math.pow(t, 2) * cp2.x +
            Math.pow(t, 3) * to.x;

        const y =
            Math.pow(1 - t, 3) * from.y +
            3 * Math.pow(1 - t, 2) * t * cp1.y +
            3 * (1 - t) * Math.pow(t, 2) * cp2.y +
            Math.pow(t, 3) * to.y;

        if (
            distanceToSegment(
                mouse.x,
                mouse.y,
                prev.x,
                prev.y,
                x,
                y
            ) < 12
        ) {
            return true;
        }

        prev = { x, y };
    }

    return false;
};


// Автоматический перенос по ширине (как в ManyChat)
export const wrapText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number
) => {
    const words = text.split(" ");
    let line = "";
    let lines: string[] = [];

    for (let i = 0; i < words.length; i++) {
        const testLine = line + words[i] + " ";
        const metrics = ctx.measureText(testLine);

        if (metrics.width > maxWidth && i > 0) {
            lines.push(line);
            line = words[i] + " ";
        } else {
            line = testLine;
        }
    }

    lines.push(line);

    // ⬇️ ВСЕГДА вниз
    lines.forEach((l, i) => {
        ctx.fillText(l, x, y + i * lineHeight);
    });
}


export const setupCanvas = (canvas: HTMLCanvasElement) => {
    console.log("setupCanvas", canvas);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    ctx.imageSmoothingEnabled = false;

    ctx.scale(dpr, dpr);
};

export const resizeCanvas = (canvas: HTMLCanvasElement) => {
    if (!canvas) return

    const parent = canvas.parentElement
    if (!parent) return
    const dpr = window.devicePixelRatio || 1;
    canvas.width = parent.clientWidth * dpr
    canvas.height = parent.clientHeight * dpr
    canvas.style.width = `${parent.clientWidth}px`
    canvas.style.height = `${parent.clientHeight}px`
}

// Функция для расчёта строк (без рисования)
export const measureWrappedText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number
) => {
    const words = text.split(" ");
    let line = "";
    const lines: string[] = [];

    for (let i = 0; i < words.length; i++) {
        const testLine = line + words[i] + " ";
        const { width } = ctx.measureText(testLine);

        if (width > maxWidth && i > 0) {
            lines.push(line.trim());
            line = words[i] + " ";
        } else {
            line = testLine;
        }
    }

    lines.push(line.trim());
    return lines;
}

// Посчитать высоту ноды
export const calculateNodeHeight = (
    ctx: CanvasRenderingContext2D,
    node: Node
) => {

    const textLines = measureWrappedText(
        ctx,
        node.text || "",
        node.w
    );

    const buttonsHeight = measureButtonsHeight(
        node.buttons
    );
    const hasButtons = node.buttons?.length;

    return (
        NODE_PADDING_TOP +
        (textLines.length + 1) * TEXT_LINE_HEIGHT +
        (hasButtons ? BUTTON_ROW_GAP : 0) +
        buttonsHeight +
        NODE_PADDING_BOTTOM
    );
}


export const measureTextHeight = (
    ctx: CanvasRenderingContext2D,
    text: string,
    nodeWidth: number,
    lineHeight: number = 16
) => {
    const lines = measureWrappedText(ctx, text, nodeWidth - 20);
    return lines.length * lineHeight;
}


export const measureButtonsHeight = (
    buttons: ButtonData[] = [],
    buttonsPerRow = 1
) => {
    if (!buttons.length) return 0;

    const rows = Math.ceil(buttons.length / buttonsPerRow);

    return (
        rows * BUTTON_HEIGHT +
        (rows - 1) * BUTTON_ROW_GAP
    );
}


export const drawButton = (
    ctx: CanvasRenderingContext2D,
    node: Node,
    button: ButtonData,
    index: number,
    buttonsStartY: number,
    layout: NodeLayout
) => {
    const width = node.w - 20;
    const x = node.x + 10;
    const y = buttonsStartY + index * (BUTTON_HEIGHT + BUTTON_ROW_GAP);

    // фон
    ctx.fillStyle = "#f8fafc";
    ctx.fillRect(x, y, width, BUTTON_HEIGHT);

    // border
    ctx.strokeStyle = "#cbd5e1";
    ctx.strokeRect(x, y, width, BUTTON_HEIGHT);

    // текст
    ctx.fillStyle = "#0f172a";
    ctx.font = "10px Inter, Arial";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";

    const buttonText = button.text ? String(button.text).slice(0, 15) : "Введите название"
    const buttonTextX = x + width * 0.2;
    const buttonTextY = y + 6;
    const buttonTextHeight = 12;
    const buttonTextWidth = width * 0.7;

    wrapText(ctx, buttonText, buttonTextX, buttonTextY, buttonTextWidth, buttonTextHeight);


    if (button.stats) {
        // CTR color
        ctx.fillStyle = "#3b82f6";
        ctx.font = "10px Inter, Arial";
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        const ctrText = "CTR " + parseInt(button.stats?.ctr?.toString() ?? "0") + "%"
        const ctrTextX = x + width * 0.7 - NODE_PADDING_LEFT;
        const ctrTextY = y + 6;
        const ctrTextHeight = 12;

        wrapText(ctx, ctrText, ctrTextX, ctrTextY, width * 0.3, ctrTextHeight);

        layout.buttonCTRRect!.push({
            x: ctrTextX,
            y: ctrTextY,
            w: width * 0.3,
            h: ctrTextHeight,
        });
    }

    return { x, y, w: width, h: BUTTON_HEIGHT };
};

export const drawConditionBranch = (
    ctx: CanvasRenderingContext2D,
    node: Node,
    branch: any,
    index: number,
    branchStartY: number
) => {
    const width = node.w - 20;
    const x = node.x + 10;
    const y = branchStartY + index * (BUTTON_HEIGHT + BUTTON_ROW_GAP);

    // фон
    ctx.fillStyle = "#f8fafc";
    ctx.fillRect(x, y, width, BUTTON_HEIGHT);

    // border
    ctx.strokeStyle = "#cbd5e1";
    ctx.strokeRect(x, y, width, BUTTON_HEIGHT);

    // текст
    ctx.fillStyle = "#0f172a";
    ctx.font = "10px Inter, Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    let conditionText = "Условие не задано";
    if (branch.conditions && branch.conditions.length > 0) {
        conditionText = branch.conditions.map((condition: any) => condition.conditionType)
            .join(branch.operator === "AND" ? " и " : " или ");
    }

    wrapText(ctx, conditionText, x + width / 2, y + 6, width - 12, 12);

    return { x, y, w: width, h: BUTTON_HEIGHT };
};

export function cubicPoint(
    p0: Point,
    p1: Point,
    p2: Point,
    p3: Point,
    t: number
): Point {
    const u = 1 - t;
    return {
        x:
            u * u * u * p0.x +
            3 * u * u * t * p1.x +
            3 * u * t * t * p2.x +
            t * t * t * p3.x,
        y:
            u * u * u * p0.y +
            3 * u * u * t * p1.y +
            3 * u * t * t * p2.y +
            t * t * t * p3.y,
    };
}


export const getMessageButtonOutPoint = (
    ctx: CanvasRenderingContext2D,
    node: Node,
    buttonIndex: number
) => {
    const textHeight = measureTextHeight(
        ctx,
        node.text || "",
        node.w
    );

    const y =
        node.y + 60 + textHeight +
        10 +
        buttonIndex * (BUTTON_HEIGHT + BUTTON_ROW_GAP) +
        BUTTON_HEIGHT / 2;

    return {
        x: node.x + node.w,
        y,
    };
};

export const getButtonRect = (
    node: Node,
    index: number,
    buttonsStartY: number
) => {
    const width = node.w - 20;
    const x = node.x + 10;
    const y = buttonsStartY + index * (BUTTON_HEIGHT + BUTTON_ROW_GAP);

    return { x, y, w: width, h: BUTTON_HEIGHT };
};


// Точка, откуда выходит стрелка из конкретной кнопки
export const getButtonOutPoint = (ctx: CanvasRenderingContext2D, node: Node, index: number) => {
    const y = node.y + NODE_PADDING_TOP + index * (BUTTON_HEIGHT + BUTTON_ROW_GAP) + BUTTON_HEIGHT / 2; // центр кнопки
    const x = node.x + node.w; // правый край Node

    return { x, y };
};

// 1 кнопка → 1 node (один исходящий edge), r
export const hasOutgoingEdge = (
    edges: Edge[],
    fromNodeID: string,
    fromButtonID?: string
) => {
    return edges.some((e) =>
        e.fromNodeID === fromNodeID &&
        e.source?.type === "button" &&
        e.source?.id === fromButtonID
    );
};

export const edgeExists = (
    edges: Edge[],
    fromNodeID: string,
    fromButtonID: string | null,
    toNodeID: string
) => {
    return edges.some(e =>
        e.fromNodeID === fromNodeID &&
        e.source?.type === "button" &&
        e.source?.id === fromButtonID &&
        e.toNodeID === toNodeID
    );
};

export const screenToWorld = (screenX: number, screenY: number, canvasStateRef: any) => {
    const { offsetX, offsetY, scale } = canvasStateRef.current;
    return {
        x: (screenX - offsetX) / scale,
        y: (screenY - offsetY) / scale,
    };
}

// Переводим их в мировые координаты
export const getMousePositions = (
    e: MouseEvent,
    canvas: HTMLCanvasElement,
    canvasStateRef: React.RefObject<{
        scale: number;
        offsetX: number;
        offsetY: number;
        scaleStep: number;
    }>
) => {
    e.preventDefault();
    const rect = canvas.getBoundingClientRect();

    // SCREEN
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;

    // WORLD
    const mouse = screenToWorld(screenX, screenY, canvasStateRef);

    return { screenX, screenY, mouse };
}

export function hitTest(node: Node, mx: number, my: number) {
    const l = node.layout;
    if (!l) return null;

    if (inRect(mx, my, l.deleteNodeIcon)) return { type: "delete-node" };
    if (inRect(mx, my, l.statsRect)) return { type: "node-stats" };
    if (inRect(mx, my, l.titleRect)) return { type: "title" };
    if (inRect(mx, my, l.textRect)) return { type: "text" };

    if (node.buttons && node.buttons?.length > 0) {
        for (let i = 0; i < l.buttonRects!.length; i++) {
            const buttonRect = l.buttonRects![i];
            if (inRect(mx, my, buttonRect)) {
                if (inCircle(mx, my, l.buttonOutPoint![i].x, l.buttonOutPoint![i].y, l.buttonOutPoint![i].w)) {
                    return {
                        type: "button-out-point",
                        index: i,
                        x: mx,
                        y: my,
                        button: node.buttons[i]
                    };
                }

                if (inRect(mx, my, l.buttonCTRRect![i])) {
                    return { type: "button-ctr", index: i, button: node.buttons[i] };
                }


                return { type: "button", index: i, button: node.buttons[i] };
            }
            if (inRect(mx, my, l.buttonDeletes![i])) {
                return { type: "button-delete", index: i, button: node.buttons[i] };
            }
        }
    }

    if (node.type === NodeType.CONDITION && node.data?.payload?.branches) {
        for (let i = 0; i < l.branchRects!.length; i++) {
            const branchRect = l.branchRects![i];
            if (inRect(mx, my, branchRect)) {
                if (inCircle(mx, my, l.branchOutPoint![i].x, l.branchOutPoint![i].y, l.branchOutPoint![i].w)) {
                    return {
                        type: "branch-out-point",
                        index: i,
                        x: mx,
                        y: my,
                        branch: node.data.payload.branches[i]
                    };
                }
                return { type: "branch", index: i, branch: node.data.payload.branches[i] };
            }
            if (inRect(mx, my, l.branchDeletes![i])) {
                return { type: "branch-delete", index: i, branch: node.data.payload.branches[i] };
            }

        }
    }
    // addBranchRect
    if (inRect(mx, my, l.addBranchRect)) {
        return { type: "add-branch" };
    }

    if (inRect(mx, my, l.addButtonRect))
        return { type: "add-button" };

    if (inCircle(mx, my, l.outPoint!.x, l.outPoint!.y, l.outPoint!.w)) {
        return { type: "output" }
    }

    return { type: "node-body" };
}

export function hitTestEdges(
    edges: Edge[],
    mx: number,
    my: number
) {
    for (let i = edges.length - 1; i >= 0; i--) {
        const e = edges[i];
        const r = e.layout?.trashRect;
        if (r && inRect(mx, my, r)) {
            return { type: "edge-trash", index: i, edge: e };
        }
    }
    return null;
}

export function inRect(mx: number, my: number, r?: Rect) {
    if (!r) return false;
    return mx >= r.x && mx <= r.x + r.w && my >= r.y && my <= r.y + r.h;
}

export function inCircle(px: number, py: number, cx: number, cy: number, r: number) {
    const dx = px - cx;
    const dy = py - cy;
    return dx * dx + dy * dy <= r * r;
}

export function drawCircle(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, color: string) {
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
}